/**
 * 트랙 일괄 작업 훅
 *
 * 트랙 목록에서 일괄 다운로드, 삭제, 재생성 작업을 처리합니다.
 * TrackListTab 컴포넌트에서 분리된 배치 로직입니다.
 */

import { useState, useCallback, useMemo } from 'react';
import React from 'react';
import { Download, RefreshCw, Trash2 } from 'lucide-react';
import type { BatchAction } from '@/components/batch';
import type { TrackDetail } from '@/lib/api/orval/models';
import { useBatchSelectionOptional } from '@/lib/stores/selectionStore';
import { downloadMultiple } from '@/lib/utils/download';
import { useToast } from './useToast';

export interface UseTrackBatchActionsOptions {
  tracks: TrackDetail[];
  onDeleteTrack: (track: TrackDetail) => void;
  onDeleteTracks?: (tracks: TrackDetail[]) => void;
  onRegenerateTrack: (track: TrackDetail) => void;
}

export interface UseTrackBatchActionsReturn {
  batchSelection: ReturnType<typeof useBatchSelectionOptional>;
  isDownloading: boolean;
  isSelectionMode: boolean;
  allTrackIds: string[];
  batchActions: BatchAction[];
  handleEnableSelectionMode: () => void;
}

export function useTrackBatchActions({
  tracks,
  onDeleteTrack,
  onDeleteTracks,
  onRegenerateTrack,
}: UseTrackBatchActionsOptions): UseTrackBatchActionsReturn {
  const { success: toastSuccess, error: toastError } = useToast();
  const batchSelection = useBatchSelectionOptional();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleEnableSelectionMode = useCallback(() => {
    batchSelection?.enableSelectionMode('track');
  }, [batchSelection]);

  // 일괄 다운로드
  const handleBatchDownload = useCallback(async (selectedIds: string[]) => {
    const selectedTracks = tracks.filter((t) => selectedIds.includes(t.id));
    const downloadItems = selectedTracks
      .filter((t) => t.audioUrl)
      .map((t) => ({
        url: t.audioUrl as string,
        filename: `${t.title || `Track ${t.trackNumber}`}.mp3`,
      }));

    if (downloadItems.length === 0) {
      toastError('다운로드 가능한 트랙이 없습니다');
      return;
    }

    setIsDownloading(true);
    toastSuccess(`${downloadItems.length}개 트랙 다운로드 시작`);

    try {
      const result = await downloadMultiple(downloadItems, 1000);
      if (result.failed > 0) {
        toastError(`${result.failed}개 다운로드 실패`);
      }
      if (result.success > 0) {
        toastSuccess(`${result.success}개 다운로드 완료`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '알 수 없는 오류';
      toastError(`일괄 다운로드 중 오류가 발생했습니다: ${message}`);
    } finally {
      setIsDownloading(false);
      batchSelection?.disableSelectionMode();
    }
  }, [tracks, toastError, toastSuccess, batchSelection]);

  // 일괄 삭제
  const handleBatchDelete = useCallback(async (selectedIds: string[]) => {
    const selectedTracks = tracks.filter((t) => selectedIds.includes(t.id));
    if (selectedTracks.length === 0) {
      return;
    }

    if (onDeleteTracks && selectedTracks.length > 1) {
      onDeleteTracks(selectedTracks);
    } else {
      onDeleteTrack(selectedTracks[0]);
    }
    batchSelection?.disableSelectionMode();
  }, [tracks, onDeleteTrack, onDeleteTracks, batchSelection]);

  // 일괄 재생성
  const handleBatchRegenerate = useCallback(async (selectedIds: string[]) => {
    const selectedTracks = tracks.filter((t) => selectedIds.includes(t.id));
    if (selectedTracks.length > 0) {
      onRegenerateTrack(selectedTracks[0]);
    }
    batchSelection?.disableSelectionMode();
  }, [tracks, onRegenerateTrack, batchSelection]);

  // 일괄 작업 정의
  const batchActions = useMemo<BatchAction[]>(() => [
    {
      id: 'download',
      label: '다운로드',
      icon: React.createElement(Download, { className: 'w-4 h-4' }),
      onClick: handleBatchDownload,
      variant: 'outline',
      loading: isDownloading,
    },
    {
      id: 'regenerate',
      label: '재생성',
      icon: React.createElement(RefreshCw, { className: 'w-4 h-4' }),
      onClick: handleBatchRegenerate,
      variant: 'outline',
    },
    {
      id: 'delete',
      label: '삭제',
      icon: React.createElement(Trash2, { className: 'w-4 h-4' }),
      onClick: handleBatchDelete,
      variant: 'danger',
    },
  ], [handleBatchDownload, handleBatchRegenerate, handleBatchDelete, isDownloading]);

  const isSelectionMode = !!(batchSelection?.state.isSelectionMode && batchSelection?.state.itemType === 'track');
  const allTrackIds = tracks.map((t) => t.id);

  return {
    batchSelection,
    isDownloading,
    isSelectionMode,
    allTrackIds,
    batchActions,
    handleEnableSelectionMode,
  };
}
